// ── Tax Monitoring ───────────────────────────────────────────────────────────
//
// Federal tax standing across the book, and the two clocks that make it urgent
// for an asset-based lender.
//
// ── WHY THIS IS NOT JUST A CREDIT SCREEN ────────────────────────────────────
//
// A tax lien is usually read as a credit problem. On a revolving line it is a
// PRIORITY problem with a deadline: IRC 6323(d) keeps our advances ahead of a
// filed NFTL only for 45 days after it was filed, or until we actually knew of
// it, whichever came first. After that, advances against new receivables and
// inventory rank behind the government — on a facility we are drawing on weekly.
//
// So the worklist is the default view, and the first column anybody sees is how
// much of that window is left.
//
// A Letter 1058 runs a second, shorter clock: 30 days and the IRS may levy. A
// lien decides who gets paid first; a levy takes the money.
//
// NOTHING HERE ORDERS ANYTHING. Every button records what is already true.
import { useState, useEffect, Fragment } from "react";
import { B } from "../../../shared/theme";
import { fmt, fmtDate, localFilter, exportCSV } from "../../../shared/format";
import { Btn, Card, ErrorMsg, FInput, Field, Modal, SearchBar, SectionHeader,
         Spinner, TabBtn, Td, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";
import { LastRunTile, runOf, useMonitorFreshness } from "../components/LastRunTile";
// Shared with the dashboard's Tax Monitoring card, so the two cannot disagree
// about how many borrowers nobody is watching.
import { EXEMPT_REASON, authState, isUnmonitored } from "../../../shared/taxCoverage";

const Tile = ({ label, value, tone, onClick, active }) => (
  <div onClick={onClick}
       style={{ background: active ? (tone || "#E1F5EE") : "var(--color-background-secondary)",
                border: "0.5px solid " + (active ? "transparent" : "var(--color-border-tertiary)"),
                borderRadius: 8, padding: "10px 14px", cursor: onClick ? "pointer" : "default", minWidth: 120 }}>
    <div style={{ fontSize: 11, color: "var(--color-text-secondary)", textTransform: "uppercase",
                  letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{value}</div>
  </div>
);

// One reading of urgency, so a row cannot look alarming in the table and calm
// in the tiles.
//
// It now has to colour rows with NO LIEN as well — a plain unpaid balance has no
// priority status to read, and neither green (which here means "our advances are
// still ahead of it") nor grey (which means "over") would be true of it. Amber:
// outstanding, no clock.
const liabilityTone = l => {
  if (l.released) return { bg: "#F1EFE8", fg: "#888780" };
  if (l.advancing_at_risk || l.tfrp_assessed) return { bg: "#FBE4DD", fg: "#993C1D" };
  if (l.priority_status && l.priority_status.startsWith("SUBORDINATE")) return { bg: "#FDF0D5", fg: "#7A5200" };
  // Inside its 45-day window. Green is a statement about PRIORITY, so only a row
  // with a filed lien can earn it.
  if (l.lien_filed) return { bg: "#E1F5EE", fg: "#0F6E56" };
  if (l.status_label === "No balance reported") return { bg: "#F1EFE8", fg: "#888780" };
  return { bg: "#FDF0D5", fg: "#7A5200" };
};

// ── GROUPED TABLE ───────────────────────────────────────────────────────────
//
// Every card on this page answers a different question with a different column
// set, but they are all read the same way: by borrower. Grouping is what makes
// three stacked tables scannable instead of three stacked walls — you find the
// company once and read down.
//
// Groups are ordered WORST FIRST by a rank each card supplies, then by name, so
// the borrower who needs something is never below the borrower who does not.
// That ordering is also why there is no per-column sort here any more: a column
// sort and a grouping both claim the row order, and the grouping wins because it
// is what somebody actually uses this page for.
const GroupedTable = ({ cols, rows, groupKey, groupLabel, groupMeta, rank, rowSort, children, empty }) => {
  if (!rows.length) {
    return <div style={{ padding: "18px 16px", fontSize: 13, color: "var(--color-text-secondary)" }}>{empty}</div>;
  }
  const map = new Map();
  for (const r of rows) {
    const k = groupKey(r) || "—";
    if (!map.has(k)) map.set(k, { key: k, label: groupLabel(r), rows: [] });
    map.get(k).rows.push(r);
  }
  const groups = [...map.values()]
    .map(g => ({ ...g, rank: Math.min(...g.rows.map(rank)) }))
    .sort((a, b) => a.rank - b.rank || String(a.label).localeCompare(String(b.label)));

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
      <thead><tr>{cols.map((c, i) =>
        <Th key={i} style={c.numeric ? { textAlign: "right" } : undefined}>{c.label}</Th>)}</tr></thead>
      <tbody>
        {groups.map(g => (
          <Fragment key={g.key}>
            {/* The house grouping band — same as AccountsPage, OpportunitiesPage
                and CollateralHome: cyanLight ground, cyanDark uppercase label,
                count beside it. Copied rather than reinvented so a borrower
                reads the same here as it does on every other grouped table. */}
            <tr>
              <td colSpan={cols.length}
                  style={{ padding: "8px 12px", fontSize: 11, fontWeight: 700,
                           textTransform: "uppercase", letterSpacing: "0.06em",
                           color: B.cyanDark, background: B.cyanLight }}>
                {g.label}
                <span style={{ marginLeft: 8, color: B.cyanDark, fontSize: 11, fontWeight: 600 }}>
                  · {g.rows.length}
                </span>
                {/* The account's status, on the band. These cards are supposed to
                    show the active book by default, and the only way to see
                    whether that is actually happening is for each group to say
                    what it is. A filter you cannot verify is a filter you have to
                    trust. */}
                {groupMeta && groupMeta(g.rows)}
              </td>
            </tr>
            {(rowSort ? [...g.rows].sort(rowSort) : g.rows).map(children)}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
};

// One card per question. The heading carries the count and the one-line reason
// the card exists, because "Coverage" on its own does not tell a new reader what
// they are looking at.
const Section = ({ title, n, blurb, tone, actions, children }) => (
  <Card style={{ padding: 0, overflowX: "auto", marginBottom: 16 }}>
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap",
                  padding: "12px 16px", borderBottom: "0.5px solid var(--color-border-tertiary)",
                  borderLeft: "3px solid " + (tone || "transparent") }}>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{title}</span>
      <span style={{ fontSize: 12.5, color: "var(--color-text-secondary)" }}>{n}</span>
      <span style={{ fontSize: 12, color: "var(--color-text-secondary)", flex: 1 }}>{blurb}</span>
      {actions}
    </div>
    {children}
  </Card>
);

export const TaxMonitoringPage = () => {
  const { api, role, go } = useApp();
  const fresh = useMonitorFreshness();   // "Last Tax Guard sync" tile
  const [data, setData] = useState({ liabilities: [], worklist: [], entities: [], reports: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [note, setNote] = useState(null);
  // The book is a minority of live credits and a majority of paid-off ones, so
  // the default is the active ones. The toggle widens the two REGISTERS; the
  // worklist stays active-only whatever this says, because there is no action to
  // take about a lien on a borrower we no longer lend to.
  const [showAll, setShowAll] = useState(false);
  // Acknowledged findings are hidden by default — that is what acknowledging is
  // for — but never gone. The toggle is how you audit what the team has set
  // aside, which matters more than the hiding does.
  const [showAcked, setShowAcked] = useState(false);
  const [acking, setAcking] = useState(null);
  // Three tabs: Needs attention, Liabilities, Coverage. They used to share one
  // page so a quiet week could not hide a problem elsewhere; now each tab label
  // carries its own warning (open findings, liens at risk, entities not
  // monitored) and every tile jumps to its tab, so nothing is out of sight.
  const [tab, setTab] = useState("work");

  const load = (all = showAll) => {
    setLoading(true); setErr(null);
    api.get("getTaxMonitoring", all ? { includeinactive: 1 } : undefined)
      .then(d => { setData(d || { liabilities: [], worklist: [], entities: [], reports: [] }); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);
  const toggleAll = () => { const next = !showAll; setShowAll(next); load(next); };

  const { liabilities, worklist, entities, reports } = data;
  const reportFor = eid => reports.find(r => String(r.borrowerguarantorid) === String(eid));

  const counts = {
    atRisk:   liabilities.filter(l => l.advancing_at_risk).length,
    // ── "OPEN LIENS" STILL MEANS LIENS ──
    // The register now carries every liability, so this has to say lien_filed
    // explicitly. Without it the tile would quietly start counting unpaid
    // balances with no notice filed, and a lender reading "open liens: 15" would
    // be reading something that is not true.
    liens:    liabilities.filter(l => l.lien_filed && !l.released).length,
    // Everything outstanding, lien or not — the number the register exists to
    // show and the one that was previously impossible to see on this page.
    unpaid:   liabilities.filter(l => !l.released && (l.amount == null || l.amount > 0)).length,
    unpaidAmt: liabilities.reduce((s, l) =>
                 s + (l.released ? 0 : (Number(l.amount) > 0 ? Number(l.amount) : 0)), 0),
    tfrp:     liabilities.filter(l => l.tfrp_assessed).length,
    levy:     liabilities.filter(l => l.levy_days_left != null && l.levy_days_left >= -30 && !l.released).length,
    unmonitored: entities.filter(isUnmonitored).length,
    exempt:   entities.filter(e => authState(e).key === "exempt").length,
    work:     worklist.filter(w => !w.acked_at).length,
  };

  // One search box over three datasets. Searching used to narrow whichever tab
  // you were on and silently leave the others; now it narrows all three, so a
  // borrower's name finds everything about them in one go.
  const FIELDS = ["companyname", "borrowerguarantorname", "subject", "detail", "form", "period",
                  "priority_status", "status_label", "monitor_type",
                  "borrowerguarantor", "entitytype", "affiliate_note", "exempt_reason", "exempt_note"];
  const fWork = localFilter(worklist, q, FIELDS).filter(w => showAcked || !w.acked_at);
  const ackedCount = worklist.filter(w => w.acked_at).length;
  const fLiabs = localFilter(liabilities, q, FIELDS);
  const fEnts = localFilter(entities, q, FIELDS);

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  // ── ACKNOWLEDGING ──
  // The fingerprint is echoed back exactly as the view computed it. It is the
  // state of the finding at the moment somebody looked, and the server refuses
  // any that is not currently on the worklist — so an acknowledgement can only
  // ever apply to a thing that was actually seen.
  const ackFinding = async (w, snooze) => {
    setAcking(w.finding_key);
    try {
      await api.post("saveTaxWorklistAck", { ack: [{
        finding_key: w.finding_key, fingerprint: w.fingerprint,
        accountid: w.accountid, borrowerguarantorid: w.borrowerguarantorid,
        subject: w.subject, detail: w.detail, snooze_until: snooze || null,
      }] });
      setNote(snooze ? "Acknowledged until " + snooze + "." : "Acknowledged. It returns if the amount or period count changes.");
      load();
    } catch (e) { setNote(e.message); }
    setAcking(null);
  };
  const unackFinding = async w => {
    setAcking(w.finding_key);
    try {
      await api.post("saveTaxWorklistAck", { clear: [w.finding_key] });
      setNote("Back on the list.");
      load();
    } catch (e) { setNote(e.message); }
    setAcking(null);
  };
  // ── EXEMPTING AN ENTITY ──
  // Portfolio managers only, and it silences the 8821 alert as well as the
  // screen, so the server gates it on a named capability rather than canEdit.
  const exemptEntity = async (e, reason) => {
    setAcking("ex-" + e.borrowerguarantorid);
    try {
      await api.post("saveTaxCoverageExemption", { exempt: [{
        borrowerguarantorid: e.borrowerguarantorid, reason,
      }] });
      setNote(e.borrowerguarantorname + " is no longer tracked for tax monitoring.");
      load();
    } catch (err) { setNote(err.message); }
    setAcking(null);
  };
  const unexemptEntity = async e => {
    setAcking("ex-" + e.borrowerguarantorid);
    try {
      await api.post("saveTaxCoverageExemption", { clear: [e.borrowerguarantorid] });
      setNote(e.borrowerguarantorname + " is back in coverage.");
      load();
    } catch (err) { setNote(err.message); }
    setAcking(null);
  };

  const plusDays = n => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

  const byCompany = r => r.accountid || r.companyname || "—";
  const label = r => r.companyname || "(no account)";

  return <>
    <SectionHeader actions={[
      <SearchBar key="s" value={q} onChange={setQ} placeholder="Search borrower, form, period..." />,
      <label key="all" title="Liabilities and Coverage only. The worklist is always the active book."
             style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5,
                      whiteSpace: "nowrap", cursor: "pointer" }}>
        <input type="checkbox" checked={showAll} onChange={toggleAll} />
        Include paid off / closed
      </label>,
      tab === "work" && <label key="acked" title="Acknowledged findings are hidden, not deleted. This is how you see what has been set aside."
             style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5,
                      whiteSpace: "nowrap", cursor: "pointer" }}>
        <input type="checkbox" checked={showAcked} onChange={e => setShowAcked(e.target.checked)} />
        Show acknowledged
      </label>,
      // Export lives on each card now: the three have different columns, so one
      // button could only ever have exported one of them.
      // Nothing on this page can show a finding against a borrower until their
      // Tax Guard client is linked to one, so the way to fix an empty screen is
      // one click from the empty screen.
      <Btn key="match" onClick={() => go("taxguard-matching")}>Match Tax Guard clients</Btn>,
      role.canEdit && <Btn key="new" variant="primary" onClick={() => setAdding(true)}>＋ Record Lien</Btn>,
    ].filter(Boolean)} />

    {showAll && (
      <div style={{ fontSize: 12, background: "#FAEEDA", border: "0.5px solid #EBD3A0",
                    borderRadius: 8, padding: "8px 12px", marginBottom: 12, color: "#7a5200" }}>
        Showing every account, including paid off and closed. The <strong>Worklist</strong> is unaffected —
        it only ever lists the active book, because a closed credit has nothing to action.
      </div>
    )}

    {note && <div style={{ fontSize: 12, background: "var(--color-background-secondary)",
                           border: "0.5px solid var(--color-border-tertiary)", borderRadius: 8,
                           padding: "8px 12px", marginBottom: 12 }}>{note}</div>}

    <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
      <Tile label="Needs attention" onClick={() => setTab("work")} value={counts.work} tone="#FDF0D5" active={counts.work > 0} />
      {/* The one that should stop a funding decision: protection gone AND a
          revolver still advancing. */}
      <Tile label="Advancing at risk" onClick={() => setTab("liabilities")} value={counts.atRisk} tone="#FBE4DD" active={counts.atRisk > 0} />
      {/* Separated from "open liens" on purpose: this is the only one that
          reaches a guarantor's personal assets. */}
      <Tile label="§6672 personal" onClick={() => setTab("liabilities")} value={counts.tfrp} tone="#FBE4DD" active={counts.tfrp > 0} />
      <Tile label="Open liens" onClick={() => setTab("liabilities")} value={counts.liens} />
      {/* The register's own total. It sat beside "Open liens" for a week saying
          the same thing, because the card behind it could only ever count liens
          — now the two differ and the gap between them is the point: a book can
          have no filed liens at all and still owe the IRS. */}
      <Tile label="Unpaid liabilities" onClick={() => setTab("liabilities")} value={counts.unpaid} tone="#FDF0D5" active={counts.unpaid > 0} />
      <Tile label="Levy notices" onClick={() => setTab("liabilities")} value={counts.levy} tone="#FBE4DD" active={counts.levy > 0} />
      <Tile label="Not monitored" onClick={() => setTab("coverage")} value={counts.unmonitored} tone="#FDF0D5" active={counts.unmonitored > 0} />
      {/* Shown only when there are any. A decision deliberately taken is worth
          seeing; a permanent zero is clutter. */}
      {!!counts.exempt && <Tile label="Not tracked (by choice)" onClick={() => setTab("coverage")} value={counts.exempt} />}
      {/* Is the daily Tax Guard sync still running? Red after 48 hours or a failed run. */}
      <LastRunTile label="Last Tax Guard sync" run={runOf.taxguard(fresh)} staleHours={48}
                   off={!!(fresh && fresh.taxguard && !fresh.taxguard.isactive)} />
    </div>

    {counts.atRisk > 0 && (
      <div style={{ fontSize: 13, color: "#993C1D", background: "#FBE4DD", border: "0.5px solid #E8B4A8",
                    borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
        <strong>{counts.atRisk} lien{counts.atRisk === 1 ? "" : "s"} where the protected window has closed
        and a revolving line is still advancing.</strong> Under IRC 6323(d) advances made now against
        after-acquired receivables and inventory rank behind the tax lien. Worth a conversation with
        counsel before the next funding.
      </div>
    )}

    {/* ── TABS ── */}
    <div style={{ display: "flex", flexWrap: "wrap", gap: 3, position: "relative", zIndex: 1, borderBottom: "4px solid " + B.cyan }}>
      <TabBtn id="work" current={tab} onClick={setTab}
              label={<>Needs Attention{counts.work ? <span style={{ marginLeft: 6, fontWeight: 700 }}>({counts.work})</span> : null}</>} />
      <TabBtn id="liabilities" current={tab} onClick={setTab}
              label={<>Liabilities{counts.atRisk || counts.tfrp ? <span style={{ marginLeft: 6, fontWeight: 700, color: tab === "liabilities" ? "#fff" : "#993C1D" }}>· {counts.atRisk + counts.tfrp} at risk</span> : null}</>} />
      <TabBtn id="coverage" current={tab} onClick={setTab}
              label={<>Coverage{counts.unmonitored ? <span style={{ marginLeft: 6, fontWeight: 700, color: tab === "coverage" ? "#fff" : "#7a5200" }}>· {counts.unmonitored} not monitored</span> : null}</>} />
    </div>
    <div style={{ background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)",
                  borderTop: "none", borderRadius: "0 0 8px 8px", padding: 16, position: "relative", zIndex: 2 }}>

    {tab === "work" && <>
    {/* ── WORKLIST ──
        The first tab, present even when empty — an empty worklist reads as
        good news, not a dead end. Liabilities and Coverage have their own tabs,
        each flagged in its label when something there needs a look. */}
    <Section title="Needs attention" n={fWork.length} tone={counts.work ? "#993C1D" : "#0F6E56"}
             blurb={"Deadlines: lien priority windows, levy clocks, lapsing authorizations."
                    + (ackedCount && !showAcked ? "  " + ackedCount + " acknowledged and hidden." : "")}
             actions={!!fWork.length && (
               <Btn onClick={() => exportCSV(fWork, ["companyname","borrowerguarantorname","subject","detail","severity","duedate"], "tax_worklist.csv")}>Export</Btn>
             )}>
      <GroupedTable
        cols={[{ label: "Entity" }, { label: "Finding" }, { label: "Detail" }, { label: "Due" }, { label: "" }]}
        rows={fWork} groupKey={byCompany} groupLabel={label}
        groupMeta={rs => {
          const st = (rs.find(r => r.accountstatus) || {}).accountstatus;
          if (!st) return null;
          const live = st === "Accrual" || st === "Non Accrual";
          return <span style={{ marginLeft: 10, fontSize: 10.5, fontWeight: 600, padding: "1px 7px",
                                borderRadius: 4, letterSpacing: 0,
                                background: live ? "#E1F5EE" : "#F1EFE8",
                                color: live ? "#0F6E56" : "#7a6f58" }}>{st}</span>;
        }}
        rank={w => w.severity || 9}
        empty={q ? "Nothing in the worklist matches that search."
                 : ackedCount ? "Nothing outstanding — " + ackedCount + " finding" + (ackedCount === 1 ? " is" : "s are")
                                + " acknowledged and hidden. Tick \u201CShow acknowledged\u201D to review them."
                 : "Nothing needs attention — no open lien windows, levy notices or lapsing authorizations. The cards below show what is being watched."}>
        {(w, i) => (
          <tr key={i} style={{ opacity: w.acked_at ? 0.55 : 1 }}>
            <Td>{w.borrowerguarantorname || <span style={{ color: "var(--color-text-secondary)" }}>account level</span>}</Td>
            <Td><span style={{ fontSize: 11.5, background: w.severity === 1 ? "#FBE4DD" : "#FDF0D5",
                               color: w.severity === 1 ? "#993C1D" : "#7A5200",
                               padding: "2px 8px", borderRadius: 4, fontWeight: 600 }}>{w.subject}</span></Td>
            <Td style={{ whiteSpace: "normal", maxWidth: 620 }}>{w.detail}</Td>
            <Td>{fmtDate(w.duedate)}</Td>
            <Td style={{ textAlign: "right" }}>
              {!role.canEdit ? null
                : w.acked_at ? (
                  <span style={{ fontSize: 11, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>
                    set aside{w.acked_by ? " by " + w.acked_by : ""}
                    {w.ack_snooze_until ? " until " + fmtDate(w.ack_snooze_until) : ""}
                    {" · "}
                    <span onClick={() => acking !== w.finding_key && unackFinding(w)}
                          style={{ cursor: "pointer", color: B.cyanDark, textDecoration: "underline",
                                   textDecorationStyle: "dotted" }}>
                      put back
                    </span>
                  </span>
                ) : (
                  <select value="" disabled={acking === w.finding_key}
                          onChange={e => {
                            const v = e.target.value;
                            if (!v) return;
                            ackFinding(w, v === "open" ? null : plusDays(Number(v)));
                          }}
                          title="Takes it off this list and stops it emailing. It comes back on its own if the amount or period count changes."
                          style={{ fontSize: 11, padding: "2px 6px", borderRadius: 5,
                                   border: "1px solid #ddd", background: "transparent",
                                   color: "var(--color-text-secondary)" }}>
                    <option value="">Acknowledge…</option>
                    <option value="open">until it changes</option>
                    <option value="30">and remind me in 30 days</option>
                    <option value="90">and remind me in 90 days</option>
                  </select>
                )}
            </Td>
          </tr>
        )}
      </GroupedTable>
    </Section>

    </>}

    {tab === "liabilities" && <>
    {/* ── LIABILITIES ──
        The register: everything reported, including what is still fine.

        This card read the lien view until now, which is defined
        WHERE lien_filed_on IS NOT NULL — so it showed 6 of the book's 15
        liabilities and the other 9 existed only as worklist findings with
        nothing behind them. A register is the thing you look something UP in;
        if it can be missing rows, the absence of a row means nothing and you go
        back to Tax Guard's portal to check, which is the behaviour this page is
        supposed to replace.

        The lien columns stay, because the 45-day window is the reason an
        asset-based lender reads this at all. They are simply blank on a row with
        no filing to run them from — rather than showing a zero that reads as a
        countdown that has expired. */}
    <Section title="Liabilities" n={fLiabs.length} tone={counts.atRisk || counts.tfrp ? "#993C1D" : undefined}
             blurb={"Every reported federal tax liability, with the IRC §6323(d) 45-day maths on those with a lien filed."
                    + (counts.unpaid ? "  " + counts.unpaid + " outstanding"
                                       + (counts.unpaidAmt > 0 ? ", " + fmt(counts.unpaidAmt) : "")
                                       + " · " + counts.liens + " with a lien filed." : "")}
             actions={!!fLiabs.length && (
               <Btn onClick={() => exportCSV(fLiabs, Object.keys(fLiabs[0] || {}), "tax_liabilities.csv")}>Export</Btn>
             )}>
      <GroupedTable
        cols={[{ label: "Entity" }, { label: "Form" }, { label: "Period" },
               { label: "Lien filed" }, { label: "Protected to" },
               { label: "Days", numeric: true }, { label: "Amount", numeric: true },
               { label: "Status" }]}
        rows={fLiabs} groupKey={byCompany} groupLabel={label}
        groupMeta={rs => {
          const st = (rs.find(r => r.accountstatus) || {}).accountstatus;
          if (!st) return null;
          const live = st === "Accrual" || st === "Non Accrual";
          return <span style={{ marginLeft: 10, fontSize: 10.5, fontWeight: 600, padding: "1px 7px",
                                borderRadius: 4, letterSpacing: 0,
                                background: live ? "#E1F5EE" : "#F1EFE8",
                                color: live ? "#0F6E56" : "#7a6f58" }}>{st}</span>;
        }}
        /* register_rank comes from dbo.vTaxLiabilityRegister. It used to be
           computed here, which was fine while the page was the only reader —
           but the CSV export, the dashboard card and any later report all need
           the same idea of "worst first", and three implementations of that is
           three chances to disagree about whether an unpaid 941 outranks a lien
           inside its window. The SQL comment lists the eight steps. */
        rank={l => (l.register_rank == null ? 9 : l.register_rank)}
        empty={q ? "No liabilities match that search." : "No tax liabilities recorded."}>
        {l => {
          const t = liabilityTone(l);
          return (
            <tr key={l.taxliabilityid}>
              <Td>{l.borrowerguarantorname || <span style={{ color: "var(--color-text-secondary)" }}>account level</span>}</Td>
              <Td>{l.form || "—"}</Td>
              {/* The "Reaches" column lived here and was removed — it read as a
                  claim about a person when it is a statement about the form, and
                  that ambiguity was worse than the information was useful. The
                  underlying classification is untouched and still drives the two
                  trust-fund branches of the worklist, where it is spelled out in
                  words rather than compressed into a badge. */}
              <Td>{l.period || "—"}</Td>
              {/* ── THE THREE LIEN COLUMNS ARE BLANK WITHOUT A FILING ──
                  An em dash, not a zero and not an empty cell. The 45 days runs
                  from the date the notice was FILED, so a liability with no
                  filing has no window — and a 0 in Days beside a red SUBORDINATE
                  row would read as a window that has just closed, which is the
                  one misreading that could stop a funding that should go ahead.
                  The dash with a tooltip says "not applicable" rather than
                  leaving somebody to guess which it is. */}
              <Td style={{ color: l.lien_filed ? "inherit" : "var(--color-text-secondary)" }}
                  title={l.lien_filed ? undefined : "No notice of federal tax lien has been filed for this liability."}>
                {l.lien_filed ? fmtDate(l.lien_filed_on) : "—"}
              </Td>
              {/* "Knew on" was removed too. The knowledge date still ENDS the
                  window early when it is earlier than filing + 45 days — it is
                  simply folded into Protected to, which is the date anybody
                  actually acts on. */}
              <Td style={{ color: l.lien_filed ? "inherit" : "var(--color-text-secondary)" }}
                  title={!l.lien_filed
                           ? "No lien filed, so there is no 45-day window to protect advances against."
                           : l.knowledge_date
                           ? "Protection ends at the earlier of the filing date plus 45 days, or the date we learned of the lien ("
                             + String(l.knowledge_date).slice(0, 10) + ")."
                           : "Filing date plus 45 days — IRC 6323(d)."}>
                {l.lien_filed ? fmtDate(l.protection_ends) : "—"}
              </Td>
              <Td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums",
                           color: l.days_of_protection_left < 0 ? "#993C1D" : "inherit",
                           fontWeight: l.days_of_protection_left < 0 ? 600 : 400 }}>
                {l.days_of_protection_left == null ? "—" : l.days_of_protection_left}
              </Td>
              <Td style={{ textAlign: "right" }}>{l.amount != null ? fmt(l.amount) : "—"}</Td>
              {/* The Levy column was removed. The 30-day Letter 1058 clock still
                  raises its own worklist item, which is where a deadline belongs
                  — the register is about lien PRIORITY, and a second countdown
                  measuring something else entirely sat beside Days inviting the
                  two to be read as one number. */}
              {/* status_label is computed in the view and already says the right
                  thing for a row with no lien ("Unpaid — no lien filed",
                  "Unpaid — installment agreement", "Unpaid — amount not
                  reported"). ADVANCING AT RISK still overrides it, because that
                  is the one state on this page that should stop a funding
                  decision and it has to be the loudest thing in the row. */}
              <Td><span style={{ fontSize: 11.5, background: t.bg, color: t.fg, padding: "2px 8px",
                                 borderRadius: 4, fontWeight: 600, whiteSpace: "nowrap" }}>
                {l.released ? "Released"
                  : l.advancing_at_risk ? "ADVANCING AT RISK"
                  : (l.status_label || l.priority_status || "—")}
              </span></Td>
            </tr>
          );
        }}
      </GroupedTable>
    </Section>

    </>}

    {tab === "coverage" && <>
    {/* ── COVERAGE ──
        The one that distinguishes "no tax problems" from "nobody is looking".
        A clean Liabilities card means one of those two and cannot say which. */}
    <Section title="Coverage" n={fEnts.length} tone={counts.unmonitored ? "#7a5200" : undefined}
             blurb="Whether a finding could reach you at all — enrolment and Form 8821."
             actions={!!fEnts.length && (
               <Btn onClick={() => exportCSV(fEnts, ["companyname","borrowerguarantorname","borrowerguarantor","entitytype","monitor_type","auth_from","auth_to","auth_checked_at","affiliate_note","exempt_reason","exempt_review_by","exempt_by"], "tax_coverage.csv")}>Export</Btn>
             )}>
      <GroupedTable
        cols={[{ label: "Entity" }, { label: "Role" }, { label: "Coverage" }, { label: "Cadence" },
               { label: "8821 to" }, { label: "Last report" }, { label: "Liability", numeric: true },
               { label: "Liens" }, { label: "" }]}
        rows={fEnts} groupKey={byCompany} groupLabel={label}
        groupMeta={rs => {
          const st = (rs.find(r => r.accountstatus) || {}).accountstatus;
          if (!st) return null;
          const live = st === "Accrual" || st === "Non Accrual";
          return <span style={{ marginLeft: 10, fontSize: 10.5, fontWeight: 600, padding: "1px 7px",
                                borderRadius: 4, letterSpacing: 0,
                                background: live ? "#E1F5EE" : "#F1EFE8",
                                color: live ? "#0F6E56" : "#7a6f58" }}>{st}</span>;
        }}
        /* Borrowers before guarantors within an account, then by name. The SQL
           orders borrowerguarantor DESC, which puts Guarantor first — fine for a
           flat list, backwards once the account is already the heading and you
           are reading down its entities. Sorted here rather than in the query
           because that ORDER BY is shared with other pages. */
        rowSort={(a, b) => {
          /* Three tiers, not two: a row with no role recorded is its own case and
             belongs at the bottom, not shuffled in among the guarantors where it
             reads as one. */
          const tier = r => r.borrowerguarantor === "Borrower" ? 0
                          : r.borrowerguarantor === "Guarantor" ? 1
                          : r.is_affiliate ? 2 : 3;
          return tier(a) - tier(b)
            || String(a.borrowerguarantorname || "").localeCompare(String(b.borrowerguarantorname || ""));
        }}
        /* Alphabetical by company, not worst-first. Coverage is the card you
           read down looking for a particular borrower — it is a roll call, not a
           queue — and a list whose order changes as authorisations lapse is one
           you have to re-scan every time. The urgency still shows in the badges;
           it just no longer moves the rows. (GroupedTable breaks a rank tie on
           the group label, so a constant rank orders by company name.) */
        rank={() => 0}
        empty={q ? "No entities match that search." : "No entities found."}>
        {e => {
          const a = authState(e);
          const rpt = reportFor(e.borrowerguarantorid);
          return (
            <tr key={e.borrowerguarantorid || "aff-" + e.taxguardclientid}
                style={{ opacity: e.exempt_reason ? 0.6 : 1 }}>
              <Td>{e.borrowerguarantorname
                   || (e.is_affiliate
                        ? <span style={{ color: "var(--color-text-secondary)" }}>
                            Tax Guard #{e.tg_client_id}
                          </span>
                        : "—")}</Td>
              <Td>
                {e.borrowerguarantor
                  ? <span title={e.is_affiliate
                                   ? "Monitored under this account without being a borrower or guarantor on it."
                                     + (e.affiliate_note ? " — " + e.affiliate_note : "")
                                   : undefined}
                          style={{ fontSize: 11.5, padding: "2px 8px", borderRadius: 4, fontWeight: 600,
                                   whiteSpace: "nowrap",
                                   background: e.is_affiliate ? "#F3EEF8"
                                             : e.borrowerguarantor === "Guarantor" ? "#EEF2F7" : B.cyanLight,
                                   color: e.is_affiliate ? "#5B4B8A"
                                        : e.borrowerguarantor === "Guarantor" ? "#4A5568" : B.cyanDark }}>
                      {e.is_affiliate ? "◇ Affiliate" : e.borrowerguarantor}
                    </span>
                  : <span style={{ color: "var(--color-text-secondary)" }}>—</span>}
                {e.entitytype && (
                  <span style={{ fontSize: 11, color: "var(--color-text-secondary)", marginLeft: 6 }}>
                    {e.entitytype}
                  </span>
                )}
              </Td>
              <Td><span style={{ fontSize: 11.5, background: a.bg, color: a.fg, padding: "2px 8px",
                                 borderRadius: 4, fontWeight: 600 }}>{a.label}</span></Td>
              <Td>{e.monitor_type || "—"}</Td>
              <Td>{fmtDate(e.auth_to)}</Td>
              <Td>{rpt ? fmtDate(rpt.report_date) : <span style={{ color: "var(--color-text-secondary)" }}>never</span>}</Td>
              <Td style={{ textAlign: "right" }}>{rpt && rpt.total_liability != null ? fmt(rpt.total_liability) : "—"}</Td>
              <Td>{rpt ? (rpt.has_liens ? "yes" : "no") : "—"}</Td>
              <Td style={{ textAlign: "right" }}>
                {/* Affiliates have no entity row, so there is nothing to exempt. */}
                {!role.canExemptTaxCoverage || e.is_affiliate ? null
                  : e.exempt_reason ? (
                    <span style={{ fontSize: 11, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>
                      {e.exempt_by ? "by " + e.exempt_by : "not tracked"}
                      {e.exempt_review_by ? " · review " + fmtDate(e.exempt_review_by) : ""}
                      {" · "}
                      <span onClick={() => acking !== "ex-" + e.borrowerguarantorid && unexemptEntity(e)}
                            style={{ cursor: "pointer", color: B.cyanDark, textDecoration: "underline",
                                     textDecorationStyle: "dotted" }}>
                        track again
                      </span>
                    </span>
                  ) : (
                    <select value="" disabled={acking === "ex-" + e.borrowerguarantorid}
                            onChange={ev => ev.target.value && exemptEntity(e, ev.target.value)}
                            title="Records that this entity does not need tax monitoring. It stops counting as a coverage gap and stops the 8821 alert chasing it."
                            style={{ fontSize: 11, padding: "2px 6px", borderRadius: 5,
                                     border: "1px solid #ddd", background: "transparent",
                                     color: "var(--color-text-secondary)" }}>
                      <option value="">Don&rsquo;t track…</option>
                      <option value="dormant">dormant entity</option>
                      <option value="covered_elsewhere">covered elsewhere</option>
                      <option value="individual">individual guarantor</option>
                      <option value="other">other</option>
                    </select>
                  )}
              </Td>
            </tr>
          );
        }}
      </GroupedTable>
    </Section>
    </>}
    </div>

    {adding && <LienForm onClose={() => setAdding(false)}
                         onSaved={m => { setAdding(false); setNote(m); load(); }} />}
  </>;
};

// ── Recording a lien by hand ────────────────────────────────────────────────
const LienForm = ({ onClose, onSaved }) => {
  const { api } = useApp();
  const [accounts, setAccounts] = useState([]);
  const [entities, setEntities] = useState([]);
  const [f, setF] = useState({
    accountid: "", borrowerguarantorid: "", form: "", period: "", amount: "",
    lien_filed_on: "", knowledge_date: "", notice_1058_on: "", source: "counsel", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = k => v => setF(x => ({ ...x, [k]: v }));

  useEffect(() => {
    api.get("getAccounts").then(d => setAccounts(Array.isArray(d) ? d : [])).catch(() => {});
    api.get("getBorrowersGuarantors").then(d => setEntities(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  const mine = entities.filter(e => String(e.accountid) === String(f.accountid));

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await api.post("saveTaxLiability", f);
      const p = res.priority;
      onSaved("Lien recorded" + (p && p.protection_ends
        ? " — advances are protected until " + String(p.protection_ends).slice(0, 10)
          + (p.advancing_at_risk ? ". THAT DATE HAS PASSED and a revolving line is still advancing." : "")
        : ""));
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const inp = { width: "100%", boxSizing: "border-box", padding: "7px 10px", fontSize: 13,
                borderRadius: 4, border: "1px solid #999" };

  return <Modal title="Record a Tax Lien" onClose={onClose} wide>
    <div style={{ padding: 20 }}>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 14 }}>
        For a lien learned of outside Tax Guard — from a UCC search, counsel, or the borrower. The 45-day
        priority window runs from the date the lien was <strong>filed</strong>, however we heard about it.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "0 16px" }}>
        <div style={{ gridColumn: "1/3" }}>
          <Field label="Borrower">
            <select value={f.accountid} style={inp}
                    onChange={e => setF(x => ({ ...x, accountid: e.target.value, borrowerguarantorid: "" }))}>
              <option value="">— Select —</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.companyname}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ gridColumn: "3/-1" }}>
          <Field label="Entity">
            <select value={f.borrowerguarantorid} disabled={!f.accountid} style={inp}
                    onChange={e => set("borrowerguarantorid")(e.target.value)}>
              <option value="">{f.accountid ? "— Select —" : "— Pick a borrower first —"}</option>
              {mine.map(e => <option key={e.id} value={e.id}>{e.borrowerguarantorname}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Form"><FInput value={f.form} onChange={set("form")} /></Field>
        <Field label="Tax period"><FInput value={f.period} onChange={set("period")} /></Field>
        <Field label="Amount"><FInput value={f.amount} onChange={set("amount")} /></Field>
        <Field label="Learned from">
          <select value={f.source} onChange={e => set("source")(e.target.value)} style={inp}>
            <option value="counsel">Counsel</option>
            <option value="manual">UCC / lien search</option>
            <option value="borrower">Borrower disclosed</option>
            <option value="legacy">Legacy record</option>
          </select>
        </Field>

        <Field label="Lien FILED on">
          <input type="date" value={f.lien_filed_on} onChange={e => set("lien_filed_on")(e.target.value)} style={inp} />
        </Field>
        {/* Defaults to today when blank. Worth setting explicitly when the lien
            was found some time before anyone got round to typing it in — the
            rule uses the earlier of this and filing + 45 days. */}
        <Field label="We knew on (blank = today)">
          <input type="date" value={f.knowledge_date} onChange={e => set("knowledge_date")(e.target.value)} style={inp} />
        </Field>
        <Field label="Letter 1058 dated">
          <input type="date" value={f.notice_1058_on} onChange={e => set("notice_1058_on")(e.target.value)} style={inp} />
        </Field>
        <div />
        <div style={{ gridColumn: "1/-1" }}>
          <Field label="Note"><FInput value={f.notes} onChange={set("notes")} /></Field>
        </div>
      </div>

      {f.lien_filed_on && (
        <div style={{ fontSize: 12.5, color: "#7a5200", background: "#FAEEDA", border: "0.5px solid #EBD3A0",
                      borderRadius: 8, padding: "10px 14px", marginTop: 10 }}>
          Advances stay ahead of this lien until{" "}
          <strong>{new Date(new Date(f.lien_filed_on).getTime() + 45 * 86400000).toISOString().slice(0, 10)}</strong>
          {f.knowledge_date && Date.parse(f.knowledge_date) < Date.parse(f.lien_filed_on) + 45 * 86400000
            && <> — or <strong>{f.knowledge_date}</strong>, the day we knew, which comes first.</>}
          {" "}Confirm the reading with counsel before funding on it.
        </div>
      )}

      {err && <div style={{ color: "#993C1D", fontSize: 12, marginTop: 8 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" disabled={busy || !f.accountid} onClick={save}>
          {busy ? "Saving…" : "Record Lien"}
        </Btn>
      </div>
    </div>
  </Modal>;
};
